import { useCallback, useMemo, useState } from 'react'
import { CameraVideo, Download } from 'react-bootstrap-icons'
import { useWidgetContext } from '../../context/WidgetContext'

const normalizeMediaUrls = (...values: unknown[]): string[] => {
  const merged: string[] = []
  const seen = new Set<string>()

  values.forEach((value) => {
    if (!Array.isArray(value)) return

    value.forEach((raw) => {
      const url = String(raw || '').trim()
      if (!url || seen.has(url)) return
      seen.add(url)
      merged.push(url)
    })
  })

  return merged
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

export const VideoPanel = () => {
  const { agent, modeApi, emitOutput } = useWidgetContext()
  const { useGenerateVideoMutation, useGetVideoStatusMutation } = modeApi as any
  const [generateVideo, { isLoading }] = useGenerateVideoMutation()
  const [getVideoStatus] = useGetVideoStatusMutation()

  const [prompt, setPrompt] = useState('')
  const [mediaUrls, setMediaUrls] = useState<string[]>([])
  const [generationId, setGenerationId] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [isPolling, setIsPolling] = useState(false)
  const [error, setError] = useState('')

  const generate = useCallback(async () => {
    const nextPrompt = prompt.trim()
    if (!nextPrompt || isLoading) return
    if (!agent.id) {
      setError('Agent is not ready.')
      return
    }

    setError('')
    setIsPolling(false)

    try {
      const response = await generateVideo({
        prompt: nextPrompt,
        agentId: agent.id,
      }).unwrap()

      const videoId = String((response as any)?.video?.id || (response as any)?.image?.id || '').trim()
      setGenerationId(videoId)
      const initialUrls = normalizeMediaUrls(
        (response as any)?.videos,
        (response as any)?.images,
        (response as any)?.video?.outputUrls,
        (response as any)?.image?.outputUrls
      )

      if (initialUrls.length > 0) {
        setMediaUrls(initialUrls)
        setSelectedIndex(0)
        emitOutput({
          type: 'video',
          output: {
            prompt: nextPrompt,
            generationId: videoId || null,
            urls: initialUrls,
            primaryUrl: initialUrls[0] || null,
            source: 'create',
          },
        })
      } else {
        setMediaUrls([])
        setSelectedIndex(0)
      }

      const shouldPoll =
        videoId.length > 0 &&
        initialUrls.length === 0 &&
        String((response as any)?.status || '').toLowerCase() !== 'failed'

      if (!shouldPoll) return
      setIsPolling(true)

      for (let attempt = 0; attempt < 25; attempt += 1) {
        await sleep(2000)
        const statusResponse = await getVideoStatus({ id: videoId, agentId: agent.id }).unwrap()
        const statusUrls = normalizeMediaUrls(
          (statusResponse as any)?.videos,
          (statusResponse as any)?.images
        )

        if (statusUrls.length > 0) {
          setMediaUrls(statusUrls)
          setSelectedIndex(0)
          emitOutput({
            type: 'video',
            output: {
              prompt: nextPrompt,
              generationId: videoId,
              urls: statusUrls,
              primaryUrl: statusUrls[0] || null,
              source: 'status',
            },
          })
          break
        }

        const status = String((statusResponse as any)?.status || '').toLowerCase()
        if (status === 'failed') {
          setError('Video generation failed.')
          break
        }
      }
    } catch {
      setError('Unable to generate video right now. Please try again.')
    } finally {
      setIsPolling(false)
    }
  }, [agent.id, emitOutput, generateVideo, getVideoStatus, isLoading, prompt])

  const primaryUrl = mediaUrls[selectedIndex] || mediaUrls[0] || ''
  const isVideo = useMemo(() => /\.(mp4|webm|mov)(?:$|\?)/i.test(primaryUrl), [primaryUrl])
  const canDownload = Boolean(primaryUrl)

  const downloadFile = useCallback((url: string, index: number) => {
    if (!url) return
    const extension = /\.(mp4|webm|mov|png|jpe?g|webp|gif|svg)(?:$|\?)/i.exec(url)?.[1] || 'bin'
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.target = '_blank'
    anchor.rel = 'noopener noreferrer'
    anchor.download = `osirus-video-${index + 1}.${extension}`
    anchor.click()
  }, [])

  return (
    <div className="osirus-media-panel osirus-media-panel-video">
      <div className="osirus-media-toolbar">
        <textarea
          value={prompt}
          onChange={(event) => setPrompt(event.target.value)}
          placeholder="Describe the video you want to generate..."
          rows={3}
        />
        <button
          type="button"
          className="osirus-media-submit"
          disabled={!prompt.trim() || isLoading}
          onClick={() => {
            void generate()
          }}
        >
          {isLoading ? 'Generating...' : 'Generate Video'}
        </button>
      </div>

      {error && <div className="osirus-generate-error">{error}</div>}

      <div className="osirus-media-workspace">
        <div className="osirus-media-canvas-card">
          <div className="osirus-media-canvas-header">
            <strong>Video Canvas</strong>
            <span>
              {isPolling
                ? 'Generating...'
                : mediaUrls.length > 0
                  ? `${mediaUrls.length} output${mediaUrls.length === 1 ? '' : 's'}`
                  : generationId
                    ? 'Generation created'
                    : 'No outputs yet'}
            </span>
          </div>

          <div className="osirus-media-canvas">
            {!primaryUrl && (
              <div className="osirus-generate-placeholder">Video preview appears here</div>
            )}
            {primaryUrl && isVideo && (
              <video className="osirus-generate-video" src={primaryUrl} controls playsInline />
            )}
            {primaryUrl && !isVideo && (
              <img src={primaryUrl} alt="Generated preview" className="osirus-generate-image" />
            )}
          </div>

          <div className="osirus-media-canvas-actions">
            <button
              type="button"
              className="osirus-media-download-btn"
              disabled={!canDownload}
              onClick={() => downloadFile(primaryUrl, selectedIndex)}
            >
              <Download />
              Download
            </button>
          </div>
        </div>

        <div className="osirus-media-output-list">
          {mediaUrls.length === 0 ? (
            <div className="osirus-media-output-empty">Generated outputs will appear here.</div>
          ) : (
            mediaUrls.map((url, index) => (
              <div
                className={`osirus-media-output-item ${index === selectedIndex ? 'is-active' : ''}`}
                key={`${url}-${index}`}
              >
                <button
                  type="button"
                  className="osirus-media-output-preview"
                  onClick={() => setSelectedIndex(index)}
                >
                  <span className="osirus-media-output-icon">
                    <CameraVideo />
                  </span>
                  <span className="osirus-media-output-title">Output {index + 1}</span>
                </button>
                <button
                  type="button"
                  className="osirus-media-output-download"
                  onClick={() => downloadFile(url, index)}
                  title={`Download output ${index + 1}`}
                >
                  <Download />
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}
